import React, { useEffect, useState } from 'react'
import { PROJECT } from 'app-constants'
import api from 'helpers/api'
import useProject from 'hooks/useProject'
import { useHubFailure, useShownHub } from 'hooks/useShownHub'
import { useApplicationStore } from 'hooks/application'
import Button from 'components/button'
import Input from 'components/input'
import ActionDialog from 'components/dialog/action'
import { FieldRow, SettingsCard } from 'views/settings/components/settingsCard'

/**
 * Replaces text across the hub's documents: their names, section titles and section content, not table
 * content. Asks to confirm, as it can't be undone.
 */
const BulkTextUpdate: React.FC = () => {
  const { project } = useProject()
  const { setSnackbarMessage } = useApplicationStore()
  const [find, setFind] = useState('')
  const [replace, setReplace] = useState('')
  const [confirming, setConfirming] = useState(false)
  const stillShown = useShownHub(project.publicId)
  const hubFailed = useHubFailure(project.publicId)

  // Another hub's settings: nothing typed or left to confirm for the last one, so it can't change this one.
  useEffect(() => {
    setFind('')
    setReplace('')
    setConfirming(false)
  }, [project.publicId])

  const apply = async () => {
    const hub = project.publicId
    const hubName = project.name
    try {
      await api.bulkReplaceDocumentText(hub, find, replace)
    } catch (error) {
      hubFailed(error, hub, `Text in ${hubName}'s documents couldn't be replaced. Open its settings to try again.`)
      throw error
    }
    // The hub named, as it may finish once another is shown; and only that hub's fields cleared.
    setSnackbarMessage({ status: 'success', message: `Replaced “${find}” with “${replace}” in ${hubName}'s documents` })
    if (!stillShown(hub)) return
    setFind('')
    setReplace('')
  }

  return (
    <SettingsCard
      title="Replace text in documents"
      description={`Replaces text in this ${PROJECT}'s document names, section titles and section content. Table content is not changed.`}
      footer={
        <>
          <p className="flex-1 text-muted">Applies to every document in this {PROJECT}, and cannot be undone.</p>
          <Button
            internalType="danger"
            data-tour="bulk-update-text-apply"
            disabled={find === '' || replace === ''}
            onClick={() => setConfirming(true)}
          >
            Replace Text
          </Button>
        </>
      }
    >
      <FieldRow label="Find">
        <Input
          aria-label="Find"
          placeholder="[CLIENT_NAME]"
          value={find}
          onChange={(event) => setFind(event.target.value)}
        />
      </FieldRow>
      <FieldRow label="Replace with">
        <Input
          aria-label="Replace with"
          placeholder="ABC Limited"
          value={replace}
          onChange={(event) => setReplace(event.target.value)}
        />
      </FieldRow>
      {confirming && (
        <ActionDialog
          title="Replace text in every document?"
          description={`Every “${find}” in this ${PROJECT}'s document names, section titles and section content becomes “${replace}”. This cannot be undone.`}
          confirmLabel="Replace Text"
          danger
          onClose={() => setConfirming(false)}
          onConfirm={apply}
        />
      )}
    </SettingsCard>
  )
}

export default BulkTextUpdate
